import React from "react";
import {
  Card,
  CardActionArea,
  CardContent,
  CardHeader,
  Typography,
  Box,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { StatusChip } from "../StatusChip";

interface SubmissionTask {
  id: string;
  status: string;
  tokenizedLink: string;
  resultsLink?: string;
  activityName: string;
  activityDueDate?: string | null;
  activityOpenDate?: string | null;
  activityStatus: string;
}

interface ReviewTask {
  id: string;
  reviewLink: string;
  reviewSubmitted: boolean;
  reviewerName: string;
  submissionId: string;
  activityName?: string;
  /** Team or student under review; null when anonymity hides it (FL-337) */
  subjectLabel?: string | null;
}

interface Props {
  type: "submission" | "review";
  submission?: SubmissionTask;
  review?: ReviewTask;
}

export function SubmissionReviewTaskCard({ type, submission, review }: Props) {
  const [t] = useTranslation();

  if (type === "submission" && submission) {
    const isSubmitted = submission.status === "SB";
    const isPending = submission.activityStatus === "SO" && !isSubmitted;
    // FL-338: scheduled activities show a card, like Team Review's
    // scheduled response cards — clicking through lands on the
    // "not yet opened" page.
    const isScheduled = submission.activityStatus === "SD";
    // FL-284: released results were only reachable from the release email —
    // the card now links straight to the student's results page.
    const resultsReleased =
      submission.activityStatus === "RL" && Boolean(submission.resultsLink);

    if (resultsReleased) {
      return (
        <Card>
          <CardActionArea
            component="a"
            href={submission.resultsLink}
            target="_blank"
            rel="noopener noreferrer"
          >
            <CardHeader
              title={submission.activityName || String(t("Submission Review"))}
              titleTypographyProps={{
                style: { color: "inherit", textDecoration: "none" },
              }}
              action={
                <StatusChip
                  status="success"
                  label={String(t("Results Released"))}
                />
              }
            />
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                {t("Click to view your results")}
              </Typography>
            </CardContent>
          </CardActionArea>
        </Card>
      );
    }

    return (
      <Card>
        <CardActionArea
          component="a"
          href={submission.tokenizedLink}
          target="_blank"
          rel="noopener noreferrer"
          disabled={isSubmitted}
        >
          <CardHeader
            title={submission.activityName || String(t("Submission Review"))}
            titleTypographyProps={{
              style: { color: "inherit", textDecoration: "none" },
            }}
            action={
              <StatusChip
                status={
                  isSubmitted
                    ? "success"
                    : isPending || isScheduled
                      ? "warning"
                      : "error"
                }
                label={
                  isSubmitted
                    ? String(t("Submitted"))
                    : isScheduled
                      ? String(t("Scheduled"))
                      : isPending
                        ? String(t("Submit Now"))
                        : String(t("Closed"))
                }
              />
            }
            subheader={
              submission.activityDueDate
                ? `${String(t("Due"))}: ${new Date(
                    submission.activityDueDate,
                  ).toLocaleDateString()}`
                : undefined
            }
          />
          {isPending && (
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                {t("Click to submit your work")}
              </Typography>
            </CardContent>
          )}
          {isScheduled && submission.activityOpenDate && (
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                {`${String(t("Opens"))}: ${new Date(
                  submission.activityOpenDate,
                ).toLocaleString()}`}
              </Typography>
            </CardContent>
          )}
        </CardActionArea>
      </Card>
    );
  }

  if (type === "review" && review) {
    return (
      <Card>
        <CardActionArea
          component="a"
          href={review.reviewLink}
          target="_blank"
          rel="noopener noreferrer"
          disabled={review.reviewSubmitted}
        >
          <CardHeader
            title={review.activityName || String(t("Peer Review"))}
            titleTypographyProps={{
              style: { color: "inherit", textDecoration: "none" },
            }}
            subheader={review.subjectLabel || undefined}
            action={
              <StatusChip
                status={review.reviewSubmitted ? "success" : "warning"}
                label={
                  review.reviewSubmitted
                    ? String(t("Completed"))
                    : String(t("Review Now"))
                }
              />
            }
          />
          {!review.reviewSubmitted && (
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                {t("Click to complete your peer review")}
              </Typography>
            </CardContent>
          )}
        </CardActionArea>
      </Card>
    );
  }

  return null;
}
